import { defaultLocale, supportedLocales } from "../config/locales";

/** Every `*TranslationDto` in the generated client is keyed by `locale`. */
export interface TranslationEntry {
    locale?: string | null;
}

/** An object carrying string values for the translatable field keys `K`. */
export type Translatable<K extends string> = {
    [P in K]?: string | null;
};

/**
 * Structural shape shared by `ActivityResponseDto`, `MeasurementTypeDto` and
 * `MeasurementValueTypeDto`: default-locale values live on the entity's own
 * fields, every other locale lives in `translations`.
 */
export type LocalizedEntity<K extends string> = Translatable<K> & {
    translations?: Array<Translatable<K> & TranslationEntry> | null;
};

/** One translatable field of an entity, e.g. an activity's `name`. */
export interface TranslatableField<K extends string> {
    /** Property name on both the entity and its `*TranslationDto`. */
    key: K;
    /** i18n key of the field's form label. */
    labelKey: string;
    type?: "text" | "textarea";
    /** i18n key of the field's placeholder, when it has one. */
    placeholderKey?: string;
    /** Rows for `type: "textarea"`. */
    rows?: number;
}

function isBlank(value: string | null | undefined): boolean {
    return value == null || value.trim().length === 0;
}

/** Reads one translatable field for `locale`, without falling back. */
export function getLocalizedValue<K extends string>(
    entity: LocalizedEntity<K> | null | undefined,
    field: K,
    locale: string
): string {
    if (entity == null) return "";
    if (locale === defaultLocale.code) return entity[field] ?? "";
    const translation = entity.translations?.find(candidate => candidate.locale === locale);
    return translation?.[field] ?? "";
}

/**
 * Returns a copy of `entity` with one translatable field set for `locale`.
 *
 * Writing the default locale updates the entity's own field; any other locale
 * updates — or appends — the matching `translations` entry. Empty rows are kept
 * while editing so a half-typed translation does not vanish mid-keystroke;
 * `pruneTranslations` drops them on save.
 */
export function setLocalizedValue<K extends string, T extends LocalizedEntity<K>>(
    entity: T,
    field: K,
    locale: string,
    value: string
): T {
    if (locale === defaultLocale.code) {
        return { ...entity, [field]: value };
    }

    const translations = entity.translations ?? [];
    const index = translations.findIndex(candidate => candidate.locale === locale);
    const next =
        index === -1
            ? [...translations, { locale, [field]: value }]
            : translations.map((translation, position) =>
                  position === index ? { ...translation, [field]: value } : translation
              );

    return { ...entity, translations: next };
}

/**
 * Drops translation rows whose translatable fields are all blank, so clearing a
 * translation in the UI deletes it server-side instead of storing empty strings.
 */
export function pruneTranslations<K extends string, T extends LocalizedEntity<K>>(
    entity: T,
    fields: ReadonlyArray<K>
): T {
    const translations = entity.translations;
    if (translations == null) return entity;

    const kept = translations.filter(
        translation => !isBlank(translation.locale) && fields.some(field => !isBlank(translation[field]))
    );

    return { ...entity, translations: kept };
}

/**
 * Locale codes (excluding the default) that carry at least one non-blank value.
 * Drives the "Translations" column and the dots on the locale tabs.
 */
export function translatedLocales<K extends string>(
    entity: LocalizedEntity<K> | null | undefined,
    fields: ReadonlyArray<K>
): string[] {
    if (entity?.translations == null) return [];
    return entity.translations
        .filter(translation => fields.some(field => !isBlank(translation[field])))
        .map(translation => translation.locale)
        .filter((locale): locale is string => locale != null);
}

/**
 * Every non-blank value across the default locale and all translations.
 * Lets table search match a record by any of its languages.
 */
export function allLocalizedValues<K extends string>(
    entity: LocalizedEntity<K> | null | undefined,
    fields: ReadonlyArray<K>
): string[] {
    if (entity == null) return [];
    const own: Array<string | null | undefined> = fields.map(field => entity[field]);
    const translated: Array<string | null | undefined> = (entity.translations ?? []).flatMap(
        translation => fields.map(field => translation[field])
    );
    return [...own, ...translated].filter((value): value is string => !isBlank(value));
}

/** True when `locale` has every translatable field filled in. */
export function isLocaleComplete<K extends string>(
    entity: LocalizedEntity<K> | null | undefined,
    fields: ReadonlyArray<K>,
    locale: string
): boolean {
    return fields.every(field => !isBlank(getLocalizedValue(entity, field, locale)));
}

/** Locales configured for this deploy target that have no values yet. */
export function missingLocales<K extends string>(
    entity: LocalizedEntity<K> | null | undefined,
    fields: ReadonlyArray<K>
): string[] {
    const present = new Set(translatedLocales(entity, fields));
    return supportedLocales
        .map(locale => locale.code)
        .filter(code => code !== defaultLocale.code && !present.has(code));
}
